/* ============================================================
   custom.js — Filthy Farmgirl brand globals
   Loaded (deferred) from snippets/custom.globals.liquid.
   Namespace + small shared helpers only. Component behaviour
   belongs in the component's own custom.* file.

   NOTE (28 Jul 2026): the quick-add relocation that used to live
   here has been REMOVED for the MVP, along with the whole quick-add
   feature. Nothing about it is lost — the working code, the three
   layout approaches that were tried, and the reasons each one
   failed are written up in claude/quick-add-parked.md, which is
   enough to rebuild it in one sitting when there's time to get it
   right. Do not reconstruct it from memory; read that doc first.
   ============================================================ */
(function () {
  'use strict';

  window.FFG = window.FFG || {};

  /* Marks the document once the brand layer is live, so CSS can
     opt in to enhancements without flashing unstyled content. */
  document.documentElement.classList.add('ffg-ready');

  /* FIREFOX-ON-iOS, STAMPED. FF iOS is WebKit, so no CSS selector or
     @supports can tell it from Safari — the UA token FxiOS is the only
     handle. Currently nothing consumes the class: the Aug-26 scroll-jump
     hunt (punch list, "FF-iOS scroll jump — PARKED") ended with the
     probe proving the jump is the browser's own toolbar-delta scroll
     compensation, and every page-side fix scoped to this class was
     reverted as ineffective. The stamp stays because it is the one
     future hook into this browser. */
  if (/FxiOS/.test(navigator.userAgent)) {
    document.documentElement.classList.add('ffg-fxios');
  }

  /* Shared: re-run callbacks when the page's geometry changes, coalesced
     to one frame.

     TWO SIGNALS, ONE LIST (12 Aug, the Firefox-sidebar bug). A window
     `resize` only fires when the WINDOW changes. Firefox's sidebar
     reshapes the content area without one: CSS media queries flipped to
     the phone regime while every resize-keyed callback slept, so the
     phone carousel rendered with the desktop relocations still inside it
     — the thumbnail-sized soap image. A ResizeObserver on <body> reports
     the box itself, whoever moved it — window, sidebar, scrollbar
     appearing, zoom tick — so the pipeline re-runs even when no resize
     event exists. Both signals feed the same frame-coalesced queue;
     echoes (a callback whose own work toggles the scrollbar and nudges
     body's width) converge because every callback here is idempotent. */
  var resizeFns = [];
  var resizeQueued = false;
  function runResizeFns() {
    if (resizeQueued) return;
    resizeQueued = true;
    window.requestAnimationFrame(function () {
      resizeQueued = false;
      for (var i = 0; i < resizeFns.length; i++) resizeFns[i]();
    });
  }
  window.FFG.onResize = function (fn) {
    resizeFns.push(fn);
  };
  window.addEventListener('resize', runResizeFns);
  if (window.ResizeObserver) {
    /* Deferred script: body exists by the time this runs. */
    new ResizeObserver(runResizeFns).observe(document.body);
  }

  /* WIDE OR NARROW IS CSS'S CALL, NEVER JS ARITHMETIC (12 Aug, same bug's
     other half). At fractional viewport widths — Devin's zoomed Firefox —
     innerWidth, matchMedia and the CSS engine's own media-query
     evaluation can disagree by a rounding hair, and a JS branch that
     disagrees with the stylesheet moves DOM into a layout that isn't
     there. --ffg-regime is written by the same 750px media query the
     layout uses (custom.css.liquid), so reading it IS asking the CSS
     engine. The matchMedia fallback only exists for the no-custom-css
     failure mode. */
  window.FFG.isWide = function () {
    var v = getComputedStyle(document.documentElement)
      .getPropertyValue('--ffg-regime')
      .trim();
    if (v) return v === 'wide';
    return window.matchMedia('(min-width: 750px)').matches;
  };

  /* ---- THE TRUE VIEWPORT WIDTH, AS A TOKEN (13 Aug) -------------------
     Devin: "the buy bar overshot a little and now sticks out into the
     yellow a few px". The bar's full-bleed anchors on 100vw, and 100vw
     includes a CLASSIC scrollbar's width — so wherever scrollbars take
     layout space (Firefox, Windows, Mac with "always show"), the anchor
     overshoots by half a scrollbar per side. documentElement.clientWidth
     is the viewport with the scrollbar already subtracted; published as
     --ffg-vw for calc() to use, with 100vw as the no-JS fallback.

     BODY, NOT documentElement (13 Aug, round two — Devin's Firefox probe:
     inner 637 == documentElement.clientWidth 637, yet #MainContent
     measured 592.4 wide and 16.5/28.1 from the edges. His Firefox scrolls
     the page via the BODY, whose classic scrollbar steals ~11.5px of
     content width without touching either root number. body.clientWidth
     is the content-available width under every scroller arrangement —
     root-scrolls, body-scrolls, wrapper-scrolls, overlay or classic —
     because body is where the content actually lives. */
  function setViewportToken() {
    document.documentElement.style.setProperty(
      '--ffg-vw',
      (document.body ? document.body.clientWidth : document.documentElement.clientWidth) + 'px'
    );
  }
  setViewportToken();
  window.FFG.onResize(setViewportToken);

  /* ---- HYPHENATED TITLES BREAK INTO TWO LINES (12 Aug) ----------------
     Devin: "a bunch of our soaps have hyphenated titles. can we create a
     global style that takes out the hyphen and instead puts the second part
     of the title on a second line in a slightly smaller font and slightly
     lighter gray."

     29 titles carry a SPACED dash ("Filthy Balls Soap - Eggnog"; the tee
     uses an em-dash) — the pattern splits on the first spaced -, – or —
     only, so hyphenated WORDS (Filthy Squirrel-Chipper) never split. The
     styling rides in .ffg-title-sub (custom.css.liquid): 0.75em at 62%
     opacity, so it reads "slightly lighter" against whatever colour the
     surface already uses.

     Idempotent by construction (a processed element contains the span and
     its first text node no longer carries a dash), so it re-runs on every
     signal that brings new titles: first load, swaps, editor re-renders,
     and a childList observer for lazy-hydrated shelves like the
     recommendations. The observer writes into its own tree — legal for the
     same reason as the variant-select's: the second pass finds no dash and
     writes nothing (the compare-before-you-write rule). */
  var TITLE_SELECTOR = [
    '.product-card p[role="heading"]',
    '.ffg-ribbon__name',
    '.product-grid h3',
    '.resource-card__title',
    '.ffg-pdp__title',
  ].join(', ');

  function splitTitles() {
    var els = document.querySelectorAll(TITLE_SELECTOR);
    Array.prototype.forEach.call(els, function (el) {
      if (el.querySelector('.ffg-title-sub')) return;
      var text = el.textContent;
      var m = text.match(/^(\s*\S[\s\S]*?)\s+[-–—]\s+(\S[\s\S]*?)\s*$/);
      /* "SMOOCHIE LIP BALM" SPLITS WITHOUT A DASH (13 Aug, Devin: "can we
         do the same for the lip balms… seems like the easy way might be to
         add a hypen to all the lip balm titles, etiher way is ok"). The
         theme way costs no store data: the exact suffix is its own split
         point, so "Banana Smoochie Lip Balm" reads Banana over the smaller
         line. Exact suffix only — "Creamy Coconut Smoochie" (no Lip Balm)
         and "Smoochie Sampler" don't match and stay one line. */
      if (!m) {
        m = text.match(/^(\s*\S[\s\S]*?)\s+(Smoochie Lip Balm)\s*$/i);
      }
      if (!m) return;
      el.textContent = m[1].replace(/^\s+/, '');
      var sub = document.createElement('span');
      sub.className = 'ffg-title-sub';
      sub.textContent = m[2];
      el.appendChild(sub);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', splitTitles);
  } else {
    splitTitles();
  }
  document.addEventListener('ffg:navigated', splitTitles);
  document.addEventListener('shopify:section:load', splitTitles);
  if (window.MutationObserver) {
    var titleQueued = false;
    new MutationObserver(function () {
      if (titleQueued) return;
      titleQueued = true;
      window.requestAnimationFrame(function () {
        titleQueued = false;
        splitTitles();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---- THE INSTRUCTIONS ART LIVES UNDER THE PRODUCT SHOTS (13 Aug) ----
     Devin, at 800px: "the instructions render way all the way down with
     the ingredients. they should be comfortably below the product shots."
     The art is born in the story band (track one, the parked story's
     slot), which sits BELOW the whole details column — and at tablet
     widths that column is long, so the art trailed far under the images.
     At ≥750 this moves the column into the product media column, directly
     after the gallery, where it hugs the images at every width. Below 750
     it goes (or stays) home in the story band — on phones the gallery is
     the top of the page, and art between the photos and the title would
     shove the buy box down. Re-run on resize (the 750 line crosses), and
     on every swap/section signal, because both homes live inside
     #MainContent and are replaced together. */
  /* THE INGREDIENTS RIDE THE SAME TRAIN (13 Aug, later: "at wide widths
     there's still a huge vertical gap between the buy bar and the
     ingredients"). Measured at 1400: the gap is the MEDIA column's height
     — image + thumbnails + art make the left side taller, and the story
     band (next section) waits below the whole grid. So the ingredients
     column moves too: into the DETAILS column at ≥750, where the right
     rail reads title → quote → chips → bar → checkout → payments →
     ingredients as one continuous stack, whatever the left side is doing.

     A comment-node marker is dropped at each element's birthplace the
     first time it moves, so shrinking below 750 puts it back EXACTLY
     where the server rendered it. Markers and movers live inside
     #MainContent and are replaced together on every swap. */
  function ffgMarkHome(el) {
    if (!el.ffgHome) {
      el.ffgHome = document.createComment('ffg-home');
      el.parentNode.insertBefore(el.ffgHome, el);
    }
  }

  function ffgSendHome(el) {
    if (el.ffgHome && el.ffgHome.parentNode && el.previousSibling !== el.ffgHome) {
      el.ffgHome.parentNode.insertBefore(el, el.ffgHome.nextSibling);
    }
  }

  function placeStoryPieces() {
    /* FFG.isWide, not matchMedia — the pieces move into containers that
       only EXIST in the wide layout, so the branch must agree with the
       stylesheet that builds it (see the sentinel note up top). */
    var wide = window.FFG.isWide();
    var art = document.querySelector('.ffg-instructions-col');
    var media = document.querySelector('.product-information__media');
    var chips = document.querySelector('.ffg-pdp-story__chips');
    var ing = chips ? chips.closest('.ffg-pdp-story__col') : null;
    var details = document.querySelector('.product-details');

    if (art && media) {
      ffgMarkHome(art);
      if (wide) {
        if (art.parentElement !== media) media.appendChild(art);
      } else {
        ffgSendHome(art);
      }
    }
    /* The trail chips ride with the instructions (21 Aug, Devin: "on wide
       screens we want them to appear under the instrutions") — appended
       AFTER art so the media column stacks art, then trails. */
    var trails = document.querySelector('.ffg-collchips-col');
    if (trails && media) {
      ffgMarkHome(trails);
      if (wide) {
        if (trails.parentElement !== media) media.appendChild(trails);
      } else {
        ffgSendHome(trails);
      }
    }
    if (ing && details) {
      ffgMarkHome(ing);
      /* NOT details itself — that computes flex ROW at desktop, and the
         first landing put the ingredients BESIDE the content column
         (measured overlapping the bar, 44px under the group's top). The
         real column is the group-block the buy bar lives in; append
         there and the stack stays vertical. */
      var bar = details.querySelector('.product-form-buttons');
      var host = (bar && bar.closest('.group-block')) || details;
      if (wide) {
        if (ing.parentElement !== host) host.appendChild(ing);
      } else {
        ffgSendHome(ing);
      }
    }

    /* The story band with both pieces gone is padding around nothing. */
    var story = document.querySelector('.ffg-pdp-story');
    if (story) {
      var grid = story.querySelector('.ffg-pdp-story__grid');
      var emptied = grid && grid.children.length === 0;
      story.classList.toggle('ffg-story-emptied', !!emptied);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', placeStoryPieces);
  } else {
    placeStoryPieces();
  }
  document.addEventListener('ffg:navigated', placeStoryPieces);
  document.addEventListener('shopify:section:load', placeStoryPieces);
  window.FFG.onResize(placeStoryPieces);

  /* ---- ANYTHING OUTSIDE THE CART TRAY CLOSES IT (10 Aug) --------------
     Devin, iPad: "a tap outside of the cart tray should close the cart
     tray. it doesn't". Two holes, one listener:

     1. ABOVE 990px THIS IS HORIZON'S DESIGN, NOT A GLITCH. theme-drawer.js
        runs the drawer as a SIDEBAR there — a non-modal dialog with no
        backdrop — and its own comment says the outside-click handler "is
        inert" in that mode. An iPad is 1024 logical px wide, so the tray
        sat open until its ✕ was found.
     2. Below 990 the modal backdrop closes on CLICK, and iPad Safari does
        not reliably synthesise one for a backdrop tap.

     A document-level pointerup covers both: if the cart dialog is open and
     the pointer landed neither inside the dialog's box nor on the element
     that opens it, close. pointerup, not click, so no-click taps still
     count; the cart icon exclusion is not strictly needed (the drawer opens
     on click, AFTER pointerup) but keeps this safe if that ever changes.
     Scoped to #cart-drawer only — the blast radius stays one tray. */
  document.addEventListener('pointerdown', function (event) {
    var dialog = document.querySelector('#cart-drawer dialog[open]');
    if (!dialog) return;
    var target = event.target;
    if (target instanceof Element) {
      if (target.closest('#cart-drawer')) {
        /* inside the tray's subtree — but a backdrop tap ALSO targets the
           dialog element itself, so fall through to coordinates for that */
        if (!(target instanceof HTMLDialogElement)) return;
      }
      if (target.closest('cart-icon, [on\\:click*="cart-drawer"]')) return;
    }
    var r = dialog.getBoundingClientRect();
    var inside =
      event.clientX >= r.left && event.clientX <= r.right &&
      event.clientY >= r.top && event.clientY <= r.bottom;
    if (inside) return;
    var host = dialog.closest('theme-drawer');
    if (host && typeof host.close === 'function') {
      host.close();
    } else {
      dialog.close();
    }

    /* THE TAP THAT CLOSES THE TRAY MUST DO NOTHING ELSE (11 Aug, then
       hardened 12 Aug — Devin, iPad: "that click still has effect on the
       page. it enlarges the product image, it places the cursor into the
       search pill"). The listener moved from pointerup to POINTERDOWN and
       calls preventDefault: focus happens at pointerdown, so only
       cancelling the down-stroke stops the search pill stealing the
       caret; on touch it also suppresses the compatibility mouse events.
       The one-shot click swallow below stays as the belt for engines
       where a click is synthesised anyway. */
    event.preventDefault();
    event.stopPropagation();

    /* BLOCK THE WHOLE TAIL OF THE TAP, not just the click (12 Aug — real
       iPad still zoomed the gallery: preventDefault on pointerdown cancels
       the click and the mouse-compat events, but Horizon's gallery acts on
       its own pointerup/touchend, which are SEPARATE events this handler's
       stopPropagation cannot reach. Every follow-on event of the closing
       tap is swallowed for 400ms, capture-phase, then the guards remove
       themselves. */
    /* ON WINDOW, NOT DOCUMENT (13 Aug — Devin, desktop: "im clicking out
       of the cart tray onto the prodcut image and triggering an enlarged
       image"). preventDefault on pointerdown suppresses the follow-on
       click for TOUCH pointers only — a mouse always delivers its click —
       and Horizon's on:click delegation listens on document, registered at
       load, so a document-level swallow registered at close-time runs
       AFTER it (same node, registration order) and stopPropagation
       arrives too late. window captures BEFORE document in the propagation
       path, so the swallow now runs first everywhere. */
    /* ONE-SHOT PER TYPE, not a 400ms blanket (15 Aug — the audit caught that
       the old version swallowed EVERY click for 400ms, so on a desktop mouse
       any *intentional* click within 400ms of closing the cart was eaten and
       the user had to click twice). The closing tap emits each of these types
       at most once, so eating the first of each type consumes exactly the
       closing tap's tail and nothing after it: each listener removes itself the
       instant it fires. The 400ms timeout is now only a cleanup for any type
       that never fired. Still window+capture, still fixes the desktop
       "close-click enlarges the product image" case — it just stops there. */
    var TAIL = ['pointerup', 'touchend', 'touchstart', 'mousedown', 'mouseup', 'click'];
    var removers = [];
    TAIL.forEach(function (type) {
      var swallow = function (e) {
        e.preventDefault();
        e.stopPropagation();
        window.removeEventListener(type, swallow, true);
      };
      window.addEventListener(type, swallow, true);
      removers.push(function () {
        window.removeEventListener(type, swallow, true);
      });
    });
    window.setTimeout(function () {
      removers.forEach(function (remove) {
        remove();
      });
    }, 400);
  });

  /* ---- THE HEART (10 Aug) --------------------------------------------
     The green bar's search icon became a heart (the pill owns search now);
     tapped, it types "we love you!" one letter at a time with the heart
     riding the end of the line like a terminal cursor, holds the thought,
     then backspaces itself back to just the heart. Markup in
     snippets/header-actions.liquid; the header survives every swap, so
     binding once at load covers the visit.

     Timing: 62ms a letter in, 26ms out — typing reads as a message,
     deleting as a tidy-up. It ignores taps while running rather than
     queueing them; a second tap should not stack two confessions. */
  (function () {
    /* TWO HEARTS IN THE MARKUP, ONE ON SCREEN (10 Aug): the drawer-side
       copy renders beside the hamburger and only exists in drawer mode;
       the actions-side copy serves desktop and hides in drawer mode via
       CSS. Bind them all — each runs independently. */
    Array.prototype.forEach.call(document.querySelectorAll('[data-ffg-heart]'), bindHeart);

  function bindHeart(heart) {
    var text = heart.querySelector('[data-ffg-heart-text]');
    if (!text) return;
    var MESSAGE = 'we love you!';
    var running = false;

    heart.addEventListener('click', function () {
      if (running) return;
      running = true;
      heart.classList.add('is-typing');
      var i = 0;

      function typeStep() {
        i++;
        text.textContent = MESSAGE.slice(0, i);
        if (i < MESSAGE.length) {
          window.setTimeout(typeStep, 62);
        } else {
          window.setTimeout(eraseStep, 1600);
        }
      }

      function eraseStep() {
        i--;
        text.textContent = MESSAGE.slice(0, i);
        if (i > 0) {
          window.setTimeout(eraseStep, 26);
        } else {
          heart.classList.remove('is-typing');
          running = false;
        }
      }

      window.setTimeout(typeStep, 120);
    });
  }
  })();
})();

/* ---- ZOOM DIALOG: A CLICK OFF THE VIEWER CLOSES IT (15 Aug, Devin) ----
   The <zoom-dialog> component only closes on the X button or Escape. A click on
   the dialog's ::backdrop (the dark area outside the image card) dispatches to
   the <dialog> element itself — target === the dialog — so catch that at the
   document and route it through the component's own close() (which keeps the
   view-transition), falling back to dialog.close(). Clicks on the card's own
   contents target child elements, never the dialog, so they don't close it. */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('dialog.dialog-zoomed-gallery__dialog')) return;
    var zd = t.closest('zoom-dialog');
    if (zd && typeof zd.close === 'function') zd.close();
    else if (typeof t.close === 'function') t.close();
  });
})();

/* ---- ZOOM DIALOG: KEEP THE ACTIVE THUMBNAIL IN STEP WITH THE SWIPE (16 Aug) ----
   The <zoom-dialog> component only syncs the selected thumbnail off ITS OWN
   handler, which listens on the dialog element — but the scroller is now the
   gallery <ul>, and scroll events don't bubble. A capture-phase document
   listener DOES see element scroll, so mirror the current slide onto the
   thumbnails' aria-selected (which drives the rounded outline). Tapping a
   thumbnail still routes through the component; this only covers manual swipes. */
/* A CLICK HIGHLIGHTS ITS OWN THUMB, INSTANTLY (24 Aug, Devin: "the
   highlight outline seems to run around to other thumbnails before
   landing on the one you've clicked"). A thumbnail tap smooth-scrolls the
   gallery, and the live mirror above dutifully highlighted every
   intermediate image on the way past — right for a finger-drag, wrong for
   a click. So a tap paints its own thumb immediately and mutes the scroll
   mirror for the glide's duration; manual swipes keep the live tracking. */
(function () {
  'use strict';

  var muteUntil = 0;

  document.addEventListener(
    'click',
    function (e) {
      var thumb = e.target instanceof Element && e.target.closest && e.target.closest('.dialog-thumbnails-list__thumbnail');
      if (!thumb) return;
      var dlg = thumb.closest('.dialog-zoomed-gallery__dialog');
      if (!dlg) return;
      muteUntil = Date.now() + 700;
      var thumbs = dlg.querySelectorAll('.dialog-thumbnails-list__thumbnail');
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].setAttribute('aria-selected', thumbs[i] === thumb ? 'true' : 'false');
      }
    },
    true
  );

  document.addEventListener(
    'scroll',
    function (e) {
      var gal = e.target;
      if (!(gal instanceof Element) || !gal.classList || !gal.classList.contains('dialog-zoomed-gallery')) return;
      if (Date.now() < muteUntil) return;
      var w = gal.clientWidth;
      if (!w) return;
      var idx = Math.round(gal.scrollLeft / w);
      var dlg = gal.closest('.dialog-zoomed-gallery__dialog');
      if (!dlg) return;
      var thumbs = dlg.querySelectorAll('.dialog-thumbnails-list__thumbnail');
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].setAttribute('aria-selected', i === idx ? 'true' : 'false');
      }
    },
    true
  );
})();

/* ---- SEAM DEBUG OVERLAY (13 Aug, temporary) -------------------------
   The iPad's footer seam cannot be reproduced in the harness, and an
   iPad has no console — so the page carries its own probe. Open any
   page with ?ffgdebug in the URL and every layer involved in the seam
   gets a coloured outline plus a readout card:

     red     .ffg-footer        (the whole footer box)
     blue    .ffg-footer__edge  (the cream hem/keyline layer)
     purple  .ffg-footer__field (the masked green)
     orange  #MainContent       (the white column whose margins are the
                                 yellow bands)

   Remove this block once the seam is settled — it renders nothing
   without the URL flag. */
  /* The ?ffgdebug seam-probe block lived here 14-22 Aug; retired with
     the seam fixed (F5, 22 Aug review). git has it if the seam returns. */

  /* GIFT CARD: A THUMBNAIL PICKS ITS CHIP (19 Aug, Devin: "when you click
     on one it would also select the corresponding chip"). Each denomination
     photo is its variant's featured media; custom-product-copy renders a
     media-id -> chip-value map (gift card PDP only, so this whole block is
     inert everywhere else). Delegated from document because the gallery
     section is re-rendered on every variant change. Checking the radio and
     letting the change event bubble runs Horizon's own variant machinery —
     price, chip highlight, and the gallery re-sort that brings the clicked
     photo to the front. */
  var giftMapEl = document.querySelector('[data-ffg-giftcard-media-map]');
  if (giftMapEl) {
    var giftMap = null;
    try {
      giftMap = JSON.parse(giftMapEl.textContent);
    } catch (mapErr) {
      giftMap = null;
    }
    if (giftMap) {
      document.addEventListener('click', function (event) {
        var thumb = event.target.closest &&
          event.target.closest('.slideshow-controls__thumbnail[data-media-id]');
        if (!thumb) return;
        var value = giftMap[thumb.getAttribute('data-media-id')];
        if (!value) return;
        var radios = document.querySelectorAll(
          '.variant-option--buttons input[type="radio"]'
        );
        for (var gi = 0; gi < radios.length; gi++) {
          if (radios[gi].value === value) {
            if (!radios[gi].checked) {
              radios[gi].checked = true;
              radios[gi].dispatchEvent(new Event('change', { bubbles: true }));
            }
            break;
          }
        }
      });
    }
  }

  /* YMAL: THE SWIPE NEVER ENDS (19 Aug, Devin: "keep cycling through
     rather than come to an end"; refined same day: "im still getting to
     the end... nice try, but still needs some refinemenet"). One clone of
     the whole slide set rides after the last soap; when the scroll
     settles a full set-width in, that width is silently subtracted —
     pixels identical modulo the set, so the wrap is invisible and the
     runway never ends, for fingers AND arrows (component.js keeps refs
     live, so the clones join the arrows' slide list and the flip-to-0
     index sits unreachably deep).

     V1'S FATAL FLAW, for the record: it observed the
     <product-recommendations> element it found at load — but hydration
     MORPHS THE WHOLE SECTION and can replace that node, leaving the
     observer bound to a detached ghost. Nothing ever fired on device.
     Now the observer rides document.body (nothing replaces that), every
     pass re-queries from the document, the scroller is derived from a
     deck slide's own parent (never a bare tag query — the cards nest
     their own slideshow-slides), and a later morph that wipes the clones
     just re-arms the whole thing. The callback is two early-exit
     querySelectors — cheap enough to leave running.

     The 140ms settle lull before teleporting is load-bearing: adjusting
     scrollLeft mid-glide kills iOS momentum. Clones are aria-hidden
     (screen readers get one honest set) but stay clickable. */
  if (document.querySelector('product-recommendations, .product-recommendations')) {
    var ymalWireLoop = function () {
      var firstSlide = document.querySelector(
        'slideshow-slide.resource-list__slide:not([data-ffg-clone])'
      );
      if (!firstSlide) return;
      var scroller = firstSlide.parentElement;
      if (!scroller || scroller.querySelector('[data-ffg-clone]')) return;
      var originals = scroller.querySelectorAll(
        'slideshow-slide.resource-list__slide:not([data-ffg-clone])'
      );
      if (originals.length < 4) return;
      var fragment = document.createDocumentFragment();
      Array.prototype.forEach.call(originals, function (slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('data-ffg-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        fragment.appendChild(clone);
      });
      scroller.appendChild(fragment);
      var firstClone = scroller.querySelector('[data-ffg-clone]');
      var settleTimer = null;
      scroller.addEventListener(
        'scroll',
        function () {
          if (settleTimer) clearTimeout(settleTimer);
          settleTimer = setTimeout(function () {
            var setWidth = firstClone.offsetLeft - firstSlide.offsetLeft;
            if (setWidth > 0 && scroller.scrollLeft >= setWidth) {
              var prevBehavior = scroller.style.scrollBehavior;
              scroller.style.scrollBehavior = 'auto';
              scroller.scrollLeft -= setWidth;
              scroller.style.scrollBehavior = prevBehavior;
            }
          }, 140);
        },
        { passive: true }
      );
    };
    ymalWireLoop();
    new MutationObserver(ymalWireLoop).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /* SWIPE CLOSES THE HAMBURGER DRAWER (20 Aug, Devin: "the hamberger menu
     should be swipable to close it"). The drawer slides in from the left,
     so a leftward swipe puts it away — through the component's own close()
     so the scroll lock releases (the empty-cart-freeze lesson). Pointer
     events rather than touch: iOS has sent them since 13, and they make
     the gesture testable headless. A mostly-vertical drag is scrolling
     and stands down. */
  var ffgHeaderDrawer = document.querySelector('header-drawer');
  if (ffgHeaderDrawer) {
    var ffgSwipeX = null;
    var ffgSwipeY = null;
    ffgHeaderDrawer.addEventListener('pointerdown', function (event) {
      var details = ffgHeaderDrawer.querySelector('details.menu-drawer-container');
      if (!details || !details.hasAttribute('open')) {
        ffgSwipeX = null;
        return;
      }
      ffgSwipeX = event.clientX;
      ffgSwipeY = event.clientY;
    }, { passive: true });
    ffgHeaderDrawer.addEventListener('pointermove', function (event) {
      if (ffgSwipeX === null) return;
      var dx = event.clientX - ffgSwipeX;
      var dy = event.clientY - ffgSwipeY;
      if (Math.abs(dy) > Math.abs(dx) + 10) {
        ffgSwipeX = null;
        return;
      }
      if (dx < -60) {
        ffgSwipeX = null;
        if (typeof ffgHeaderDrawer.close === 'function') ffgHeaderDrawer.close();
      }
    }, { passive: true });
    ffgHeaderDrawer.addEventListener('pointerup', function () {
      ffgSwipeX = null;
    }, { passive: true });
  }

  /* THE HEART AND "PRODUCTS" SHARE ONE HOVER MAP (21 Aug, Devin: "if you
     slide over to the heart you leave the mapped area for the drop down
     and it folds up. i want the heart and the word 'proudcts' to share a
     map"). The CSS bridge (batch 199) keeps the panel PAINTED while the
     heart is hovered, but Horizon's menu JS still runs its close path the
     moment the pointer leaves the list item — so the heart now forwards
     its hover INTO the component, using the same synthetic-pointerenter
     dispatch the component's own Safari-reconciliation code uses on
     itself. Entering the heart (re)activates Products; leaving toward
     anything else closes through the normal machinery. */
  var ffgCornerHeart = document.querySelector('.header__column--left > .ffg-heart--corner');
  /* F4 (22 Aug review): an unguarded :has() in querySelector THROWS on
     old engines and kills the rest of this file — including the
     pill-placeholder unveil below, which would leave the search pill
     permanently blank there. Guarded, like atc-stitch's selectWraps. */
  var ffgProductsItem = null;
  try {
    ffgProductsItem = document.querySelector('.menu-list__list-item:has(.menu-list__submenu)');
  } catch (hasErr) {
    ffgProductsItem = null;
  }
  if (ffgCornerHeart && ffgProductsItem) {
    ffgCornerHeart.addEventListener('pointerenter', function () {
      ffgProductsItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    });
  }

  /* The pill's placeholder unveils only when Staatliches is real — see
     the ffg-pillfont rule in custom.css.liquid. fonts.ready is the
     belt-and-braces: whatever happens (blocked font, ancient browser),
     the placeholder never stays hidden past font settlement. */
  if (document.fonts && document.fonts.load) {
    document.fonts.load('16px Staatliches').then(function (faces) {
      if (faces.length) document.documentElement.classList.add('ffg-pillfont');
    }).catch(function () {});
    document.fonts.ready.then(function () {
      document.documentElement.classList.add('ffg-pillfont');
    });
  } else {
    document.documentElement.classList.add('ffg-pillfont');
  }

/* ---- MEDIA <-> CHIP, BOTH WAYS (24 Aug, rounds one-three) -------------
   Devin: "right now if you select a different image nothing changes in the
   buy bar... i want it to go both ways... the back of the bar should be
   wired to the large bar chip." Piloted on Filthy Pussy, the first soap
   with a full image set.

   Chip -> image (a DIRECT chip tap) is our smooth scroll since round five
   (the selected-first sort that used to do it reshuffled the thumbnails —
   see the trusted-change listener below and ffg_selected_first in
   product-media-gallery-content.liquid).
   This is the other direction: the slideshow marks the visible slide with
   aria-hidden="false", so one attribute observer catches every way the
   image can change — swipe, arrows, thumbnail tap. The map (emitted by
   custom-product-copy.liquid: media id -> chip value, value -> variant id)
   is RE-READ per event, never cached at module load — the sampler-prices
   lesson: a global module initialises on the visit's landing page, and a
   cached map is null forever after a no-reload navigation.

   WHY THE SELECTION IS SILENT (round three — the first build dispatched a
   real change event and Devin caught both consequences: "still reshuffling
   the images after every image selection" and the zoom dialog "closes the
   enlarged image view box" two seconds after a click). Horizon answers a
   change event by morphing the WHOLE product section: the gallery
   re-sorts to put the variant's media first (the reshuffle, under the
   shopper's thumb mid-swipe), and the zoom dialog — DOM inside that
   section — is destroyed when the fetch lands (~2s). data-skip-node-update
   cannot fence the gallery off: morph.js honours it for a node's OWN
   attributes and still recurses into children (morph.js:248). So an
   image-initiated selection goes around the machinery instead of through
   it: check the radio with NO event, then hand-sync the two things that
   matter — every product form's variant id input (main form and any
   sticky bar copy) and the URL's ?variant=. The chips restyle themselves
   (:has(input:checked) is CSS), the chip prices are per-chip and static,
   and the bar's name/price pair is retired — so nothing visible goes
   stale. Known trade: a SOLD-OUT variant selected this way keeps the live
   ADD TO CART (Horizon's sold-out re-label rides the morph we skip);
   Shopify still refuses the add at the cart, and no current soap in the
   pilot is sold out.

   Scoped to slides inside <media-gallery> (the PDP gallery only): product
   cards use card-gallery and the YMAL deck is a resource list. A 200ms
   settle keeps a fast swipe from machine-gunning through the intermediate
   slides. The 500ms suppress window after a DIRECT chip tap keeps
   Horizon's own scroll-to-variant-image (which flips slides on the way)
   from echoing back into a silent re-selection. */
(function () {
  'use strict';

  var settleTimer = null;
  var suppressUntil = 0;

  function readMap() {
    var el = document.querySelector('[data-ffg-media-chip-map]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function silentSelect(value, map) {
    var radios = document.querySelectorAll('.variant-option--buttons input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value !== value) continue;
      /* Compare before you write — the no-op is what breaks any loop. */
      if (radios[i].checked) return;
      radios[i].checked = true;

      /* HORIZON'S OWN BOOKKEEPING RIDES ALONG (24 Aug, round four — Devin:
         "if from 3 pack i select large bar, it now shows BOTH as selected").
         The chip CSS matches :has(input:checked, [data-current-checked=
         "true"]) — TWO trackers. variant-picker.js maintains the dataset
         pair on every real change (lines ~225-230); a silent select that
         moves only `checked` leaves data-current-checked stranded on the
         old chip, and the two chips both dress selected, pointers and all.
         Mirror it: current on the target, current=false and previous=false
         everywhere else (previous only feeds the pill slide, which this
         theme disables — false is its rest state). */
      for (var k = 0; k < radios.length; k++) {
        radios[k].dataset.currentChecked = radios[k] === radios[i] ? 'true' : 'false';
        radios[k].dataset.previousChecked = 'false';
      }

      /* The two hand-syncs a skipped morph owes the page. */
      var variantId = map.variants && map.variants[value];
      if (variantId) {
        var inputs = document.querySelectorAll('form[action*="/cart/add"] input[name="id"], .shopify-product-form input[name="id"]');
        for (var j = 0; j < inputs.length; j++) inputs[j].value = variantId;
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('variant', variantId);
          window.history.replaceState(window.history.state, '', u.toString());
        } catch (e) {
          /* the URL is a nicety, never worth failing over */
        }
      }
      return;
    }
  }

  function mediaFor(value, map) {
    if (!map) return null;
    /* toMedia (26 Aug, the Pirate bug): the straight value -> media read.
       The reverse scan below only worked while photos and variants were
       one-to-one; Pirate's six chips share three photos, and a reverse
       read of a deduped map leaves most chips unanswered. The scan stays
       as the fallback for maps cached in HTML from before the change. */
    if (map.toMedia && map.toMedia[value]) return map.toMedia[value];
    if (!map.media) return null;
    for (var id in map.media) {
      if (map.media[id] === value) return id;
    }
    return null;
  }

  /* THROUGH THE COMPONENT, NOT THE SCROLLER (24 Aug, round six — Devin's
     iPhone: tap 3-Pack, then swipe, and "it shows you HALF a large bar").
     The first version hand-computed a scrollLeft, which ignores the
     carousel's snap geometry — desktop engines quietly re-snap after a
     programmatic scroll, iOS Safari does NOT: the scroller parks off-snap
     and the next swipe inherits the broken offset. slideshow-component's
     own select() is snap-aware and keeps its internal index honest — the
     same call Horizon's arrows and the zoom sync use (media-gallery.js
     calls select(index, undefined, {animate})). The raw-scroll fallback
     stays for the day the component API moves. */
  function scrollGalleryTo(mediaId, behavior) {
    var media = document.querySelector('media-gallery [data-media-id="' + mediaId + '"]');
    var slide = media ? media.closest('slideshow-slide') : null;
    if (!slide || slide.getAttribute('aria-hidden') === 'false') return;

    var component = slide.closest('slideshow-component');
    if (component && typeof component.select === 'function' && slide.parentElement) {
      /* Index among sibling slides — the component's slides[] refs are
         exactly the slideshow-slide children of its one scroller. */
      var kids = slide.parentElement.children;
      var idx = -1;
      var n = 0;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].tagName === 'SLIDESHOW-SLIDE') {
          if (kids[i] === slide) idx = n;
          n++;
        }
      }
      if (idx >= 0) {
        try {
          component.select(idx, undefined, { animate: behavior !== 'auto' });
          return;
        } catch (e) {
          /* fall through to the raw scroll */
        }
      }
    }

    var scroller = slide.parentElement;
    if (!scroller) return;
    var target = scroller.scrollLeft + slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    try {
      scroller.scrollTo({ left: target, behavior: behavior });
    } catch (e) {
      scroller.scrollLeft = target;
    }
  }

  /* A DIRECT chip tap (real change event, trusted) briefly suppresses the
     observer — its glide flips slides on the way, and those flips must not
     read as the shopper choosing images.

     THE GLIDE IS OURS NOW (24 Aug, round five — Devin: "selecting a
     different chip reorganizes the thumbnails"). Chip -> image used to be
     Horizon's selected-first media sort: every variant change re-rendered
     the strip with the variant's photo promoted to slot one, reshuffling
     the thumbnails. That sort is dead-switched in
     product-media-gallery-content.liquid (ffg_selected_first), so the
     gallery keeps one fixed order for the whole visit and a chip tap
     answers with a smooth SCROLL to the variant's slide instead — same
     destination, no musical chairs. */
  document.addEventListener('change', function (event) {
    var t = event.target;
    if (t && t.matches && t.matches('.variant-option--buttons input[type="radio"]') && event.isTrusted) {
      suppressUntil = Date.now() + 700;
      var map = readMap();
      var mediaId = mediaFor(t.value, map);
      if (mediaId) scrollGalleryTo(mediaId, 'smooth');

      /* NORMALISE THE BOOKKEEPING AFTER EVERY HANDLER RUNS (24 Aug —
         Devin: "if i click that new chip again it gives me the double
         selection bug"). Horizon's picker updates its dataset pair from
         ITS OWN selection history, which never saw a silent select — so a
         real tap sets current=true on the tapped chip but clears it only
         from Horizon's stale notion of "previous", leaving current=true
         stranded on the silently-chosen chip: two dressed chips again.
         checked is the single source of truth (radios are one native
         group; the browser keeps exactly one), so mirror the dataset to
         it after the picker's own change handler has had its turn.
         (morph.js:18 lists data-current-checked among the attributes the
         morph never syncs, so this client-side state is never fought.) */
      window.setTimeout(function () {
        var radios = document.querySelectorAll('.variant-option--buttons input[type="radio"]');
        for (var i = 0; i < radios.length; i++) {
          radios[i].dataset.currentChecked = radios[i].checked ? 'true' : 'false';
        }
      }, 0);
    }
  }, true);

  /* ARRIVING ON ?variant= LANDS ON ITS PHOTO. The server used to answer a
     variant URL with that photo sorted first; with the order fixed, the
     gallery opens on slide one regardless — so an arrival scroll (instant,
     no animation: a real navigation arrives, it does not travel) puts the
     linked variant's image on screen. Covers hard loads and no-reload
     swaps alike; a page without the map does nothing. */
  function ffgArrivalScroll() {
    if (window.location.search.indexOf('variant=') === -1) return;
    var map = readMap();
    if (!map) return;
    var checked = document.querySelector('.variant-option--buttons input[type="radio"]:checked');
    if (!checked) return;
    var mediaId = mediaFor(checked.value, map);
    if (mediaId) scrollGalleryTo(mediaId, 'auto');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ffgArrivalScroll);
  } else {
    ffgArrivalScroll();
  }
  document.addEventListener('ffg:navigated', ffgArrivalScroll);

  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var el = muts[i].target;
      if (!(el instanceof Element)) continue;
      if (el.tagName !== 'SLIDESHOW-SLIDE') continue;
      if (el.getAttribute('aria-hidden') !== 'false') continue;
      if (!el.closest('media-gallery')) continue;
      if (Date.now() < suppressUntil) break;
      var media = el.querySelector('[data-media-id]');
      if (!media) continue;
      var mediaId = media.getAttribute('data-media-id');
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        if (Date.now() < suppressUntil) return;
        var map = readMap();
        if (!map || !map.media) return;
        /* SHARED PHOTOS MAKE image -> chip AMBIGUOUS (26 Aug, Pirate: three
           Boy chips feature one photo). If the photo on screen already
           belongs to the SELECTED chip, this is not the shopper choosing a
           different product — it is the glide from their own chip tap
           settling (or a swipe landing on what they already have). Leaving
           the selection alone is the only read that is never wrong; without
           this, a chip-driven glide that outlived the suppress window
           re-mapped the photo to its FIRST claimant and flipped the
           shopper's 3-Pack back to Large. */
        var checked = document.querySelector('.variant-option--buttons input[type="radio"]:checked');
        if (checked && map.toMedia && map.toMedia[checked.value] === mediaId) return;
        var value = map.media[mediaId] || map.default;
        if (!value) return;
        /* MULTI-OPTION (26 Aug, the tee: Colour x Size): option1 alone
           cannot name a variant id, so the silent path's hand-sync of the
           form has nothing to write — instead check the colour radio and
           dispatch a REAL change, and Horizon resolves colour+size, form
           id, URL and picker morph itself. Safe since Batch 244: the
           media-set guard stops the gallery replaceWith that made real
           changes destructive. The synthetic event is untrusted, so the
           trusted-chip listener above ignores it — no scroll loop. */
        if (map.multi) {
          var radios = document.querySelectorAll('.variant-option--buttons input[type="radio"]');
          for (var r = 0; r < radios.length; r++) {
            if (radios[r].value !== value) continue;
            if (!radios[r].checked) {
              radios[r].checked = true;
              radios[r].dispatchEvent(new Event('change', { bubbles: true }));
            }
            break;
          }
          return;
        }
        silentSelect(value, map);
      }, 200);
      break;
    }
  }).observe(document.body, { attributes: true, attributeFilter: ['aria-hidden'], subtree: true });
})();

/* ---- EMPTY CART: "CONTINUE SHOPPING" CLOSES THE DRAWER (26 Aug, Devin:
   "when you open the cart when it's empty and then click continue
   shopping, it should just close the cart not take you on an adventure
   to the all products page"). The button is a plain <a> to the all-
   products collection (snippets/cart-products.liquid), which is right on
   the /cart PAGE — there is nothing to close and the shopper asked to go
   somewhere — but wrong in the DRAWER, where "continue shopping" means
   "put this panel away, I was mid-browse". The split is the ancestor:
   inside <theme-drawer> (which owns the dialog lifecycle and exposes
   close()), swallow the navigation and close; anywhere else the anchor
   behaves like the anchor it is. Capture phase, so the intercept wins
   before the browser commits to the navigation. */
(function () {
  'use strict';
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var button = target.closest('.cart-items__empty-button');
    if (!button) return;
    var drawer = button.closest('theme-drawer');
    if (!drawer || typeof drawer.close !== 'function') return;
    event.preventDefault();
    drawer.close();
  }, true);
})();
